import {computed, effect, inject, Injectable, InjectionToken, Signal, signal, WritableSignal} from '@angular/core';
import {patchState, signalState} from '@ngrx/signals';
import {ITab} from './tab.interface';
import {OnTabActivated} from 'frontier/browserkit';
import {IControlObject} from "frontier/nucleus";

export
type TabState = {
  tabs: ITab[];
  selectedTabIndex: number;
}

const LOCAL_STORAGE_KEY = 'tabs';
const localStorageTabs: string = localStorage.getItem(LOCAL_STORAGE_KEY);

export const INITIAL_TABS_TOKEN = new InjectionToken<TabState>('initialTabs');
export const COMPONENT_REGISTRY_TOKEN = new InjectionToken<{ [key: string]: any }>('componentRegistry');

@Injectable({
  providedIn: 'root'
})
export class TabsService {
  componentRegistry: { [p: string]: any } = inject(COMPONENT_REGISTRY_TOKEN, {optional: false});
  initialTabsToken: TabState = inject(INITIAL_TABS_TOKEN, {optional: false});

  initialState: TabState = localStorageTabs ? JSON.parse(localStorageTabs) : this.initialTabsToken;

  readonly state = signalState<TabState>(this.initialState);
  readonly tabs: Signal<ITab[]> = this.state.tabs;
  readonly selectedTabIndex: Signal<number> = this.state.selectedTabIndex;
  readonly selectedTab: Signal<ITab> = computed((): ITab => this.tabs()[this.selectedTabIndex()]);
  readonly animationDuration: WritableSignal<number> = signal(300);

  constructor() {
    console.log('Initial tabs:', this.initialTabsToken);
    effect((): void => {
      const state: TabState = this.state();
      const copy: TabState = {
        ...state, tabs: state.tabs.map((tab: ITab): ITab => {
          const tabCopy = {...tab};
          delete tabCopy.componentInstance;
          return tabCopy;
        })
      };
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(copy));
    });

    effect((): void => {
      const selectedTab: ITab = this.selectedTab();

      if (selectedTab) {
        // Activate the tab
        if ((<OnTabActivated>selectedTab.componentInstance)?.onTabActivated) {
          setTimeout((): void => {
            (<OnTabActivated>selectedTab.componentInstance).onTabActivated();
          }, this.animationDuration() + 50);
        }
      }
    });
  }

  openTab(label: string, component: string, tabId: string, alertObjects: IControlObject[] = []): void {
    patchState(this.state, (state: TabState): TabState => {
      return {
        tabs: [...state.tabs, {label, componentName: component, tabId, alertObjects}],
        selectedTabIndex: state.tabs.length,
      }
    })
  }

  selectTabById(label: string): void {
    patchState(this.state, (state: TabState): { selectedTabIndex: number } => {
      return {
        selectedTabIndex: state.tabs.findIndex((tab: ITab): boolean => tab.label === label),
      }
    })
  }

  selectTabByIndex(index: number): void {
    patchState(this.state, (): { selectedTabIndex: number } => {
      return {
        selectedTabIndex: index,
      }
    })
  }

  closeTabByIndex(index: number): void {
    patchState(this.state, (state: TabState): TabState => {
      return {
        tabs: state.tabs.filter((_, i: number): boolean => i !== index),
        // If the selected tab is closed, select the previous tab
        selectedTabIndex: index === state.selectedTabIndex ? Math.max(0, state.selectedTabIndex - 1) : state.selectedTabIndex,
      }
    })
  }

  closeAllTabs(): void {
    patchState(this.state, (): TabState => {
      return {
        tabs: [],
        selectedTabIndex: 0,
      }
    })
  }

  closeAllTabsExcept(tab: ITab): void {
    patchState(this.state, (): TabState => {
      return {
        tabs: [tab],
        selectedTabIndex: 0,
      }
    })
  }

  updateTabsOrder(tabsArray: ITab[]): void {
    patchState(this.state, (state: TabState): TabState => {
      const selectedTabId: string = state.tabs[state.selectedTabIndex]?.tabId;
      const newSelectedIndex: number = tabsArray.findIndex((t: ITab): boolean => t.tabId === selectedTabId);

      return {
        tabs: tabsArray,
        selectedTabIndex: newSelectedIndex !== -1 ? newSelectedIndex : state.selectedTabIndex
      };
    });
  }
}
